import ValidationErrors from '@/components/validation-errors';
import { new_session_path, registration_path } from '@/rails/routes';
import type { PageProps } from '@/types';
import { Button } from '@astryxdesign/core/Button';
import { Card } from '@astryxdesign/core/Card';
import { Heading } from '@astryxdesign/core/Heading';
import { HStack } from '@astryxdesign/core/HStack';
import { Link } from '@astryxdesign/core/Link';
import { Text } from '@astryxdesign/core/Text';
import { TextInput } from '@astryxdesign/core/TextInput';
import { VStack } from '@astryxdesign/core/VStack';
import { useForm, usePage } from '@inertiajs/react';
import { ArrowRight, LockKeyhole, Mail, User } from 'lucide-react';
import type { FormEvent } from 'react';

interface Props extends PageProps {
  errors: string[];
  formData: {
    email?: string;
    name?: string;
  };
}

const NewRegistration = () => {
  const { errors, formData } = usePage<Props>().props;
  const form = useForm({
    name: formData.name ?? '',
    email: formData.email ?? '',
    password: '',
  });

  const nameError = errors.find((error) => /name|nombre/i.test(error));
  const nameStatus = nameError ? { type: 'error' as const, message: nameError } : undefined;
  const emailError = errors.find((error) => /email|correo/i.test(error));
  const emailStatus = emailError ? { type: 'error' as const, message: emailError } : undefined;
  const passwordError = errors.find((error) => /password|contraseña/i.test(error));
  const passwordStatus = passwordError
    ? { type: 'error' as const, message: passwordError }
    : undefined;

  const fieldErrors = [nameError, emailError, passwordError].filter(Boolean);
  const bannerErrors =
    fieldErrors.length > 0 ? errors.filter((error) => !fieldErrors.includes(error)) : errors;

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    form.transform((data) => ({ user: data }));
    form.post(registration_path());
  };

  return (
    <main className="iorio-auth-page">
      <VStack as="section" gap={8} width="100%" className="iorio-auth-shell">
        <HStack vAlign="center" width="100%" className="iorio-auth-brand-row">
          <HStack gap={2} vAlign="center">
            <img src="/iorio-icon.png" alt="" className="iorio-auth-mark" />
            <Text type="body" weight="bold" size="lg">
              Iorio
            </Text>
          </HStack>
        </HStack>

        <section className="iorio-auth-grid" aria-label="Creación de cuenta en Iorio">
          <Card padding={8} width="100%" className="iorio-auth-card">
            <form onSubmit={submit}>
              <VStack gap={5} hAlign="stretch">
                <ValidationErrors errors={bannerErrors} />

                <VStack gap={2}>
                  <Heading level={1}>Crear cuenta</Heading>
                </VStack>

                <VStack gap={3}>
                  <TextInput
                    label="Nombre"
                    htmlName="name"
                    placeholder="Tu nombre"
                    value={form.data.name}
                    onChange={(value) => form.setData('name', value)}
                    size="lg"
                    startIcon={User}
                    isRequired
                    hasAutoFocus
                    status={nameStatus}
                  />

                  <TextInput
                    label="Email"
                    type="email"
                    htmlName="email"
                    placeholder="nombre@empresa.com"
                    value={form.data.email}
                    onChange={(value) => form.setData('email', value)}
                    size="lg"
                    startIcon={Mail}
                    isRequired
                    status={emailStatus}
                  />

                  <TextInput
                    label="Contraseña"
                    type="password"
                    htmlName="password"
                    placeholder="Tu contraseña"
                    value={form.data.password}
                    onChange={(value) => form.setData('password', value)}
                    size="lg"
                    startIcon={LockKeyhole}
                    isRequired
                    description="Al menos 8 caracteres"
                    status={passwordStatus}
                  />
                </VStack>

                <Button
                  label="Crear cuenta"
                  variant="primary"
                  size="lg"
                  type="submit"
                  icon={<ArrowRight size={18} />}
                  isDisabled={form.processing}
                  isLoading={form.processing}
                />

                <Text type="supporting" color="secondary">
                  ¿Ya tenés cuenta?{' '}
                  <Link href={new_session_path()} type="supporting" hasUnderline>
                    Entrar a Iorio
                  </Link>
                </Text>
              </VStack>
            </form>
          </Card>
        </section>
      </VStack>
    </main>
  );
};

export default NewRegistration;
